import {
  COOKIES,
  FIXED_PRODUCT_IDS,
  FORM_IDS,
  REWARD_TIERS,
} from '@bulknutrients/bulk-shared-lib/constants';
import {
  type BaseProduct,
  isFullProduct,
  isSuggestFlavoursForm,
  type Product,
  type Variant,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import { isVariantAvailable } from '@bulknutrients/bulk-shared-lib/lib/product';
import { Accordion } from '@components/Accordion';
import { SignUpModal } from '@components/Account/SignUpModal';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { AddToCart, AddToFavourites, Button } from '@components/Button';
import { AddToCartAccountRequired } from '@components/Button/AddToCart';
import { Select } from '@components/FormComponents';
import { Icons } from '@components/Icons/Icons';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import { Modal } from '@components/Modal/Modal';
import { SocialShares } from '@components/Social';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useKlaviyo } from '@hooks/useKlaviyo';
import { useStockLevel } from '@hooks/useStockLevel';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { BulkBuy } from '../BulkBuy';
import { FlavourDescriptions } from '../FlavourDescriptions';
import { NotifyStockVariant } from '../NotifyStock';
import { Options, ProductSampleOptions } from '../Options';
import { ProductPrice, ProductPricing } from '../ProductPrice';
import { Quantity } from '../Quantity';
import { StarRating } from '../StarRating';
import { SuggestAFlavour } from '../SuggestAFlavour';
import { USPComponent } from '../USP';

function ProductOverview({
  starRatings,
  description,
  ingredients,
}: {
  starRatings: { title: string; value: number }[];
  description: string;
  ingredients: { title: string; value: boolean }[];
}) {
  return (
    <div className="pt-2">
      <p className="text-sm">{description}</p>
      <div className="grid grid-cols-2 text-sm">
        <ProductDetails starRatings={starRatings} />
        <DietaryInformation ingredients={ingredients} />
      </div>
    </div>
  );
}

function ProductDetails({
  starRatings,
}: {
  starRatings: { title: string; value: number }[];
}) {
  return (
    <div>
      {starRatings.map((i, idx) => (
        <p
          key={`star${idx}`}
          className="my-1 flex items-center justify-start gap-1"
        >
          <StarRating rating={i.value} />
          {i.title}
        </p>
      ))}
    </div>
  );
}

function DietaryInformation({
  ingredients,
}: {
  ingredients: { title: string; value: boolean }[];
}) {
  return (
    <div>
      {ingredients.map((i, idx) => (
        <p
          key={`ingredient${idx}`}
          className="my-1 flex items-center justify-start"
        >
          <span className="icon mr-1">
            {i.value && (
              <Icons.CheckCircle className="check text-green dark:text-white" />
            )}
            {!i.value && (
              <Icons.Close className="cross text-red dark:text-white" />
            )}
          </span>
          {i.title}
        </p>
      ))}
    </div>
  );
}

function RewardsAndInstalments({
  loggedIn,
  price,
  title,
}: {
  loggedIn: boolean;
  price: number;
  title: string;
}) {
  return loggedIn ? (
    <>
      <h3 className="m-0 pt-2 text-left text-base font-bold text-black dark:text-white">
        Point Rewards
      </h3>
      <p className="mb-4 text-sm">
        You're on your way to earning at least{' '}
        {Math.floor(price * REWARD_TIERS.WELCOME.earn)} points when you purchase{' '}
        {title}. <TextLink href="/rewards">Find out more</TextLink>.
      </p>
      <h3 className="m-0 py-0 text-left text-base font-bold text-black dark:text-white">
        Pay in 4 Payments
      </h3>
      <p className="text-sm">
        For bigger orders over $100 AUD, choose to pay with AfterPay or PayPal
        Pay in 4. <TextLink href="/payments">Find out more</TextLink>.
      </p>
    </>
  ) : (
    <>
      <h3 className="m-0 pt-2 text-left text-base font-bold text-black dark:text-white">
        Point Rewards
      </h3>
      <p className="mb-4 text-sm">
        <LoginModalUncontrolled
          trigger={
            <Button buttonStyle="text" className="inline">
              Sign in
            </Button>
          }
          title={<>Login to your account</>}
        />{' '}
        or <TextLink href="/create-account">register an account</TextLink> to
        earn {Math.floor(price * REWARD_TIERS.WELCOME.earn)} points when you
        purchase {title}.
      </p>
      <p className="mb-4 text-sm">
        When you reach the Bronze tier, you'll earn{' '}
        {Math.floor(price * REWARD_TIERS.BRONZE.earn)} points, at the Silver
        tier earn {Math.floor(price * REWARD_TIERS.SILVER.earn)}, and{' '}
        {Math.floor(price * REWARD_TIERS.GOLD.earn)} points on the Gold Tier.{' '}
        <TextLink href="/rewards">Find out more</TextLink>.
      </p>
      <h3 className="m-0 py-0 text-left text-base font-bold text-black dark:text-white">
        Pay in 4 Payments
      </h3>
      <p className="text-sm">
        For bigger orders over $100 AUD, choose to pay with AfterPay or PayPal
        Pay in 4. <TextLink href="/payments">Find out more</TextLink>.
      </p>
    </>
  );
}

function Availability({
  product,
  inStock,
  stockLevels,
  partialStock,
}: {
  product: BaseProduct;
  inStock?: boolean;
  stockLevels: Record<number, boolean> | undefined;
  partialStock: boolean;
}) {
  return (
    <div className="pt-2">
      {product.minPurchaseQty > 1 && (
        <p className="mb-4 text-sm">
          The minimum quantity purchasable of {product.name} is{' '}
          {product.minPurchaseQty}.
        </p>
      )}
      {product.maxPurchaseQty < 9999 && (
        <p className="mb-4 text-sm">
          The maximum quantity purchasable of {product.name} is{' '}
          {product.maxPurchaseQty}.
        </p>
      )}
      {partialStock && (
        <p className="mb-4 text-sm">
          Some options are not available to purchase of {product.name}.<br />
        </p>
      )}
      {isFullProduct(product) &&
      product.availability &&
      product.availability.length > 0 ? (
        <p className="mb-4 text-sm">{product.availability}</p>
      ) : (
        <p className="mb-4 text-sm">
          {inStock !== undefined &&
            inStock &&
            `In stock for same day dispatch when you order before 3pm ${
              new Date()
                .toLocaleString('en-AU', {
                  timeZone: 'Australia/Hobart',
                  timeZoneName: 'long',
                })
                .includes('Daylight')
                ? 'AEDT'
                : 'AEST'
            } (Monday to Friday)`}
          {inStock !== undefined &&
            !inStock &&
            'Currently out of stock. Check back soon or request a notification'}
        </p>
      )}
      <p className="text-sm">
        <TextLink href="/faqs/shipping">
          Flat rate $9 shipping Australia-wide.
        </TextLink>{' '}
        Orders up to 3.5kg ship Express Post, over 3.5kg ship Parcel Post.
      </p>
    </div>
  );
}

export function ProductInformation({
  product,
  inStock,
  stockLevels,
  ...props
}: {
  product: BaseProduct;
  inStock?: boolean;
  stockLevels: Record<number, boolean> | undefined;
} & Record<string, any>) {
  const [selectedVariant, setSelectedVariant] = useState<
    Variant | null | undefined
  >();
  const { customer } = useCustomer();

  const router = useRouter();
  const watchOptionChanges = props.rhfMethods.watch();
  const quantity = props.rhfMethods.watch('quantity');
  const setQuantity = (q) => props.rhfMethods.setValue('quantity', q);

  const isDigital = product.sku.includes('BN-EBOOK');

  // Have to check that variants are available, based on banter etc
  const availableVariants = product.variants.filter((v) =>
    isVariantAvailable(v, customer ? customer.customerGroupId : undefined)
  );

  const descriptionsAvailable =
    availableVariants.filter((v) => v.flavourDescription).length > 0;

  // Work out if global or partial or no stock
  let partialStock = false;
  if (stockLevels) {
    partialStock = Object.entries(stockLevels).reduce((acc, [key, value]) => {
      if (availableVariants.find((v) => v.variantId === Number.parseInt(key))) {
        return acc || !value;
      }
      return acc;
    }, false);
  }

  // ensure quantity resets to 1 when the product changes
  useEffect(() => setQuantity(1), [product.entityId]);

  // When a valid variant has been selected, set it and enable add to cart
  useEffect(() => {
    let mounted = true;

    const selectedOptions = props.rhfMethods.getValues(); // {optionId: valueId}
    // when options changed, work out which variant that is
    const v = availableVariants.find(
      (availableVariant) =>
        availableVariant.options.length > 0 && // this is for dodgy data (when variants removed, usually affects staging)
        availableVariant.options.every(
          (opt) =>
            selectedOptions[opt.optionId.toString()] === opt.valueId.toString()
        )
    );

    if (v && mounted) {
      setSelectedVariant(v); // set the variant as selected (for adding to cart)
    } else if (availableVariants.length === 1) {
      // if only one variant option, set as default to enable add to cart
      setSelectedVariant(availableVariants[0]);
    } else {
      setSelectedVariant(null);
    }

    return function cleanup() {
      mounted = false;
    };
  }, [
    availableVariants,
    product.options,
    watchOptionChanges,
    props.rhfMethods,
  ]);

  const renderAvailabilityIcon = () => {
    if (product.minPurchaseQty > 1 || product.maxPurchaseQty < 9999) {
      return <Icons.Alert color="hsl(33, 80%, 52%)" />;
    }

    if (inStock && partialStock) {
      return <Icons.Alert color="hsl(33, 80%, 52%)" />;
    }

    if (inStock && !partialStock) {
      return <Icons.CheckCircle className="fill-green dark:fill-green-light" />;
    }

    // checking for false because inStock satisfies !inStock when undefined and defaults to red.
    if (inStock === false) {
      return <Icons.Error color="hsl(0, 100%, 50%)" />;
    }

    return null;
  };

  const items = [] as Array<{
    title: string | React.ReactNode;
    content: React.ReactNode;
    props?: Record<string, any>;
  }>;

  if (isFullProduct(product)) {
    items.push({
      title: 'Product Overview',
      content: (
        <ProductOverview
          starRatings={product.starRatings}
          description={
            typeof product.shortDescription === 'string'
              ? product.shortDescription
              : product.shortDescription.join('\n')
          }
          ingredients={product.toggles}
        />
      ),
      props: { 'data-testid': 'product-overview' },
    });

    if (!product.discontinued) {
      items.push({
        title: 'Rewards & Easy Instalments',
        content: (
          <RewardsAndInstalments
            loggedIn={customer ? true : false}
            price={product.pricing.priceRange.min.value}
            title={product.shortName ?? product.name}
          />
        ),
      });
    }

    items.push({
      title: (
        <span className="flex items-center gap-2">
          Availability & Shipping {renderAvailabilityIcon()}
        </span>
      ),
      content: (
        <Availability
          product={product}
          inStock={inStock}
          partialStock={partialStock}
          stockLevels={stockLevels}
        />
      ),
    });

    if (
      (product.pricing.bulkPricing.length > 0 ||
        (selectedVariant && selectedVariant.pricing.bulkPricing.length > 0)) &&
      !product.disableBulkBuy &&
      !product.discontinued
    ) {
      items.push({
        title: (
          <span className="flex items-center gap-2">
            Multibuy Discounts
            <Icons.CheckCircle className="fill-green dark:fill-green-light" />
          </span>
        ),
        content: (
          <BulkBuy
            bulkPricing={product.pricing.bulkPricing}
            variants={product.variants}
            servingSize={product.servingSize}
            packageDescription={product.packageDescription}
            maxPurchaseQty={product.maxPurchaseQty}
            hasFlavours={product.options.some(
              (o) =>
                o.displayName.toLowerCase().includes('flavour') &&
                o.values.length > 1
            )}
            title={product.shortName ?? product.name}
          />
        ),
      });
    }
  }

  return (
    <section>
      {isFullProduct(product) &&
        !props.compactOverride &&
        product.usp &&
        product.usp.length > 0 && <USPComponent usps={product.usp} />}

      {product.discontinued ? null : (
        <div className="pt-5" aria-label="Pricing">
          <ProductPricing
            pricing={
              selectedVariant ? selectedVariant.pricing : product.pricing
            }
          />
        </div>
      )}
      {product.discontinued ? null : (
        <>
          <div
            className={
              props.offScreen
                ? 'xl:bg-grey-light xl:dark:bg-grey-darkest xl:w-[400px] xl:fixed xl:flex xl:flex-col xl:z-1 xl:drop-shadow-lg xl:dark:drop-shadow-black xl:bottom-4 xl:p-4 xl:ml-4 rounded-2xl'
                : 'mt-2'
            }
          >
            <Options
              options={product.options}
              variants={availableVariants}
              stockLevels={stockLevels}
              displayOptionsLabel={!props.compactOverride}
            />
            {descriptionsAvailable && (
              <FlavourDescriptions
                variants={availableVariants}
                product={product}
              />
            )}
            {isFullProduct(product) &&
              !props.compactOverride &&
              product.suggestFlavours &&
              isSuggestFlavoursForm(product.suggestFlavours) && (
                <SuggestAFlavour
                  form={product.suggestFlavours}
                  productName={product.name}
                  productId={product.entityId}
                />
              )}

            <div className="mt-4 flex items-end gap-3 flex-row flex-wrap">
              <Quantity
                inStock={inStock}
                quantity={quantity}
                setQuantity={setQuantity}
                min={product.minPurchaseQty}
                max={
                  product.maxPurchaseQty && product.maxPurchaseQty > 0
                    ? product.maxPurchaseQty
                    : 9999
                }
              />
              {/* border is added to match the height of quantity */}
              {inStock && !isDigital && (
                <div>
                  <AddToCart
                    queryID={router?.query ? router.query.queryID : ''}
                    productId={product.entityId}
                    name={product.name}
                    variantId={
                      selectedVariant?.variantId ||
                      product.variants[0].variantId
                    }
                    sku={selectedVariant?.sku || product.variants[0].sku}
                    disabled={
                      (selectedVariant === null &&
                        !!product.options &&
                        product.options.length > 0) ||
                      quantity === 0
                    }
                    quantity={quantity}
                    triggerCartModal={props.triggerCartModal}
                    tooltipText={
                      (selectedVariant === null &&
                        !!product.options &&
                        product.options.length > 0) ||
                      quantity === 0
                        ? 'Select an option above'
                        : 'Add to cart'
                    }
                    data-umami-event-location="ProductInformation on product page"
                  />
                </div>
              )}
              {inStock !== undefined && (
                <div className="border border-solid border-transparent flex items-center">
                  <AddToFavourites
                    sku={product.sku}
                    productId={product.entityId}
                    data-umami-event-location="productPageInformation"
                  />
                </div>
              )}
            </div>
            {product.addToCartMessage && !props.compactOverride ? (
              <p className="pt-4 text-base">{product.addToCartMessage}</p>
            ) : null}
          </div>

          {stockLevels &&
          Object.values(stockLevels).some((level) => level === false) ? (
            <NotifyStockVariant
              variants={product.variants}
              stockLevels={stockLevels}
              options={product.options.filter((o) => o.values.length > 1)}
              availability={
                isFullProduct(product) ? product.availability : undefined
              }
            />
          ) : null}

          {!props.compactOverride && <hr className="mt-4 text-grey-mid" />}
        </>
      )}
      {isFullProduct(product) && !props.compactOverride && (
        <BulkErrorBoundary location="Product Information Accordion">
          <div className="mb-4">
            <Accordion
              items={items.map((i) => ({
                ...i,
                content: (
                  <div className="border-b pb-4 border-grey-mid">
                    {i.content}
                  </div>
                ),
                className: 'font-bold pt-2.5',
                props: {
                  ...i.props,
                },
              }))}
              openIndexes={[items.length - 1]}
            />
          </div>
        </BulkErrorBoundary>
      )}
      {!props.compactOverride && (
        <div className="flex h-10 items-center justify-between">
          <SocialShares
            type={'product'}
            shareSlug={product.slug}
            titleText={product.name}
            imageSource={
              isFullProduct(product) && !props.compactOverride
                ? product.defaultImgUrl
                : ''
            }
          />
          <Button
            href={props.productDetailsLink ?? '#Product-Description'}
            buttonStyle="text"
          >
            Full product details
          </Button>
        </div>
      )}
    </section>
  );
}

export function ProductInformationSamplesPage({
  product,
  samplePack,
  inStock,
  stockLevels,
  ...props
}: {
  product: Product;
  samplePack: Product;
  inStock?: boolean;
  stockLevels: Record<number, boolean> | undefined;
} & Record<string, any>) {
  const { customer } = useCustomer();
  const { freeSampleRequestedSubscribe } = useKlaviyo();
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const { inStock: packInStock } = useStockLevel(
    samplePack.entityId.toString()
  );

  const formValues = useWatch({
    control: props.rhfMethods.control,
    defaultValue: props.rhfMethods.getValues(),
  });

  // Have to check that variants are available, based on banter etc
  const availableVariants = product.variants.filter(
    (v) =>
      isVariantAvailable(v, customer ? customer.customerGroupId : undefined) &&
      v.availableAsSample
  );

  const selectedVariant = useMemo<{
    productId: number;
    variantId: number;
  } | null>(() => {
    const variantId = formValues?.sampleVariantId;
    if (!variantId) {
      return null;
    }

    return {
      productId: FIXED_PRODUCT_IDS.freeSample,
      variantId,
    };
  }, [formValues?.sampleVariantId]);

  const items = [] as Array<{
    title: string | React.ReactNode;
    content: React.ReactNode;
    props?: Record<string, any>;
  }>;

  if (isFullProduct(product)) {
    items.push({
      title: 'Product Overview',
      content: (
        <ProductOverview
          starRatings={product.starRatings}
          description={
            typeof product.shortDescription === 'string'
              ? product.shortDescription
              : product.shortDescription.join('\n')
          }
          ingredients={product.toggles}
        />
      ),
      props: { 'data-testid': 'product-overview' },
    });
  }

  const handleSampleRequest = async () => {
    if (!customer) {
      // No customer, sign up or sign in
      setShowSignUpModal(true);
      setSubmitting(false);
      return;
    }

    // Check the shipping address
    if (!formValues.selectedAddress) {
      // No address chosen, pick one
      setShowAddressForm(true);
      return;
    }

    // Got what we need, move on
    completeSampleRequest();
  };

  const completeSampleRequest = async () => {
    setSubmitting(true);
    if (!selectedVariant) {
      setError(true);
      setErrorMessage('Please select a sample option before requesting.');
      setSubmitting(false);
      return;
    }

    const shippingAddress = customer?.addresses.find(
      (a) => a.id === formValues.selectedAddress
    );

    if (!shippingAddress) {
      setError(true);
      setErrorMessage('Please select a shipping address before requesting.');
      setSubmitting(false);
      return;
    }

    if (customer) {
      const { data, error } = await fetch('/api/form/free-sample', {
        method: 'GET',
      }).then((res) => res.json());

      // signed in and have already had a sample
      if (!error && data) {
        setError(true);
        setErrorMessage(
          "Our records show that you've already requested a free sample. If you believe this is an error, please contact our support team."
        );
        setSubmitting(false);
        return;
      }

      // signed in and haven't had a sample so we can request it
      const sampleName = product.variants
        .find((v) => v.sampleVariantId == selectedVariant.variantId)
        ?.options.filter(
          (o) => o.displayName.toLowerCase() === 'flavour'
        )[0]?.label;

      if (!sampleName) {
        setError(true);
        setErrorMessage(
          <div>
            There was an error with the sample you selected. Please try again or{' '}
            <TextLink href="/contact-us">contact us</TextLink>.
          </div>
        );
        setSubmitting(false);
        return;
      }

      await freeSampleRequestedSubscribe({
        email: customer.email,
        freeSample: `${product.name} - ${sampleName}`,
        source: 'Product Sample page',
      });

      const response = await fetch('/api/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId: FORM_IDS.FREE_SAMPLE,
          formData: {
            firstName: shippingAddress?.firstName || '',
            lastName: shippingAddress?.lastName || '',
            company: shippingAddress?.company || '',
            email: customer?.email || '',
            country: shippingAddress?.country || '',
            postcode: shippingAddress?.postcode || '',
            state: shippingAddress?.state || '',
            suburb: shippingAddress?.suburb || '',
            address1: shippingAddress?.address1 || '',
            address2: shippingAddress?.address2 || '',
            phone: shippingAddress?.phone || '',
            requested_sample: `${product.name} - ${sampleName}`,
          },
        }),
      });

      if (!response.ok) {
        const { error } = await response.json();
        setError(true);
        setErrorMessage(
          <div>
            There was an error with requesting the sample you selected. Please
            try again or <TextLink href="/contact-us">contact us</TextLink>.
            <pre>{error.message.join('. ')}</pre>
          </div>
        );
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      setSuccess(true);
    }

    // if not signed in, prompt to create account and prefill form with selected sample
    if (!customer) {
      setShowSignUpModal(true);
      setSubmitting(false);
    }
  };

  return (
    <section>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <SuccessAlert show={success} setShow={setSuccess}>
        <p className="dark:text-white">
          Thanks! We've received your free sample request 🥳 It should arrive in
          10-14 days.
        </p>
      </SuccessAlert>
      <Modal show={showAddressForm} setShow={setShowAddressForm}>
        <div>
          {!customer?.addresses || customer.addresses.length === 0 ? (
            <p>
              No addresses available. Please{' '}
              <TextLink href="/account/addresses">add a new address</TextLink>{' '}
              to your account.
            </p>
          ) : (
            <>
              <p className="pb-2">
                Select the address you want the sample sent to or{' '}
                <TextLink href="/account/addresses">add a new address</TextLink>{' '}
                to your account.
              </p>
              <Select
                name="selectedAddress"
                label="Shipping Address"
                options={customer?.addresses.map((address) => ({
                  value: address.id,
                  label: `${address.address1}, ${address.suburb}, ${address.state}, ${address.postcode}, ${address.country}`,
                }))}
              />
              <Button
                onClick={() => {
                  setShowAddressForm(false);
                  completeSampleRequest();
                }}
              >
                Ok
              </Button>
            </>
          )}
        </div>
      </Modal>
      <SignUpModal
        showModal={showSignUpModal}
        setShowModal={setShowSignUpModal}
        leadCopy={
          <>
            You'll need to create an account or{' '}
            <LoginModalUncontrolled
              trigger={
                <Button buttonStyle="text" className="inline">
                  login
                </Button>
              }
              title={<>Login to your account</>}
            />{' '}
            to request a sample, but don't worry, it's quick and easy!
          </>
        }
        successMessage={
          <p className="dark:text-white">
            Welcome aboard! You're all signed up and should receive an email to
            set your password. In the meantime, you can now request your sample
            🥳
          </p>
        }
      />
      {isFullProduct(product) &&
        !props.compactOverride &&
        product.usp &&
        product.usp.length > 0 && <USPComponent usps={product.usp} />}

      {product.discontinued ? null : (
        <>
          <div
            className={
              props.offScreen
                ? 'xl:bg-grey-light xl:dark:bg-grey-darkest xl:w-[400px] xl:fixed xl:flex xl:flex-col xl:gap-4 xl:z-1 xl:drop-shadow-lg xl:dark:drop-shadow-black xl:bottom-4 xl:p-4 xl:ml-4 rounded-2xl'
                : 'mt-5 flex flex-col gap-4'
            }
          >
            <p>
              Try a mixed 7-day trial pack for $
              {samplePack.pricing.price.value % 1 === 0
                ? samplePack.pricing.price.value
                : samplePack.pricing.price.value.toFixed(2)}{' '}
              + free shipping (save $9)
            </p>
            <AddToCartAccountRequired
              productId={samplePack.entityId}
              variantId={samplePack.variants[0].variantId}
              quantity={1}
              sku={samplePack.variants[0].sku}
              name={samplePack.name}
              disabled={!packInStock}
              successCallback={() => {
                // store a cookie
                const checkout_check_cookie = Cookies.get(
                  COOKIES.freeSamplePageFreeShipping
                );
                if (!checkout_check_cookie) {
                  Cookies.set(COOKIES.freeSamplePageFreeShipping, uuid());
                }
              }}
            />
            <p>
              Or request a free single sample of a flavour of your choice below.
            </p>
            {product.discontinued || props.compactOverride ? null : (
              <div className="">
                <ProductPrice basePrice={0} price={0} />
              </div>
            )}
            <ProductSampleOptions
              variants={availableVariants}
              stockLevels={stockLevels}
            />
            <Button
              className="w-fit"
              onClick={handleSampleRequest}
              disabled={submitting}
            >
              {submitting ? 'Requesting...' : 'Request'}
            </Button>

            {product.addToCartMessage && !props.compactOverride ? (
              <p className="pt-4 text-base">{product.addToCartMessage}</p>
            ) : null}
          </div>
          {!props.compactOverride && <hr className="mt-4 text-grey-mid" />}
        </>
      )}
      {isFullProduct(product) && !props.compactOverride && (
        <BulkErrorBoundary location="Product Information Accordion">
          <div className="mb-4">
            <Accordion
              items={items.map((i) => ({
                ...i,
                className:
                  'font-bold pt-2.5 px-0 clickable no-underline hover:underline active:underline',
                props: {
                  ...i.props,
                },
              }))}
              defaultOpen={true}
              openIndexes={[items.length - 1]}
            />
          </div>
        </BulkErrorBoundary>
      )}
    </section>
  );
}
